import { FC, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Grid,
  LabelValue,
  Link,
  Select,
  Stack,
  TextField,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { useRequestHouseShipmentCancellation } from '@/features/shipments'
import { useLocalisation } from '@/utils/localisation'

import { CANCELLATION_REASON_LABEL_KEYS, CancellationReason } from './cancellationReasons'
import { getErrorMessage } from './getErrorMessage'

export type CancelShipmentDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  houseShipmentID: string
  cargoReadyDate?: string
  contactPerson?: string
  origin?: string
  destination?: string
  quoteCode?: string | null
}

export const CancelShipmentDialog: FC<CancelShipmentDialogProps> = ({
  open,
  onOpenChange,
  houseShipmentID,
  cargoReadyDate,
  contactPerson,
  origin,
  destination,
  quoteCode,
}) => {
  const { translate, translateDate } = useLocalisation()
  const toaster = useToaster()
  const cancelMutation = useRequestHouseShipmentCancellation()
  const [reason, setReason] = useState<string | undefined>(undefined)
  const [otherReasonText, setOtherReasonText] = useState<string | undefined>(undefined)

  const isOtherSelected = reason === CancellationReason.Other
  const trimmedOtherReason = otherReasonText?.trim()
  const reasonToSubmit = (isOtherSelected ? trimmedOtherReason : reason) ?? null
  const isSubmitDisabled = cancelMutation.isPending

  const reasonOptions = useMemo(
    () =>
      Object.values(CancellationReason).map((value) => ({
        value,
        label: translate(CANCELLATION_REASON_LABEL_KEYS[value]),
      })),
    [translate]
  )

  const handleClose = (): void => {
    setReason(undefined)
    setOtherReasonText(undefined)
    onOpenChange(false)
  }

  const handleReasonChange = (newReason: string | undefined): void => {
    setReason(newReason)
    if (newReason !== CancellationReason.Other) {
      setOtherReasonText(undefined)
    }
  }

  const handleConfirm = async (): Promise<void> => {
    try {
      await cancelMutation.mutateAsync({
        houseShipmentID,
        reason: reasonToSubmit,
      })
      toaster({
        title: translate('notifications.cancel_shipment.notifications.success.title'),
        description: translate('notifications.cancel_shipment.notifications.success.description'),
        variant: 'positive',
      })
    } catch (e) {
      toaster({
        title: translate('notifications.cancel_shipment.notifications.error.title'),
        description: getErrorMessage(e, translate),
        variant: 'negative',
      })
    } finally {
      handleClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth="screen-md" maxHeight="large">
        <DialogHeader>
          <DialogTitle>{translate('notifications.cancel_shipment.title')}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <Stack gap={16}>
            <Alert
              variant="primary"
              title={
                <Stack gap={4}>
                  <Typography variant="body-bold">
                    {translate('notifications.cancel_shipment.intro.heading')}
                  </Typography>
                  <Typography variant="body">
                    <Trans
                      i18nKey="notifications.cancel_shipment.intro.description"
                      t={translate}
                      components={[
                        <Link
                          key="terms"
                          href={translate('signup-invited.fields.terms_link')}
                          target="_blank"
                          rel="noopener noreferrer"
                        />,
                      ]}
                    />
                  </Typography>
                </Stack>
              }
            />

            <Stack gap={12}>
              <Typography variant="body-bold">{translate('notifications.cancel_shipment.summary.heading')}</Typography>
              <Grid col={2} gap={16}>
                <LabelValue
                  label={translate('notifications.cancel_shipment.summary.price_at_booking')}
                  value={
                    quoteCode ? (
                      <Trans
                        i18nKey="notifications.cancel_shipment.summary.price_at_booking_quote"
                        t={translate}
                        values={{ quoteCode }}
                        components={[
                          <Link
                            key="quote"
                            href={`/quotes?highlight=${encodeURIComponent(quoteCode)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          />,
                        ]}
                      />
                    ) : (
                      translate('notifications.cancel_shipment.summary.price_at_booking_fak')
                    )
                  }
                />
                <LabelValue
                  label={translate('notifications.cancel_shipment.summary.crd_at_booking')}
                  value={cargoReadyDate ? translateDate('cargo_ready_date', new Date(cargoReadyDate)) : '-'}
                />
                <LabelValue
                  label={translate('notifications.cancel_shipment.summary.contact_person')}
                  value={contactPerson ?? '-'}
                />
                <div />
                <LabelValue label={translate('notifications.cancel_shipment.summary.origin')} value={origin ?? '-'} />
                <LabelValue
                  label={translate('notifications.cancel_shipment.summary.destination')}
                  value={destination ?? '-'}
                />
              </Grid>
            </Stack>

            <Select
              label={translate('notifications.cancel_shipment.reason.label')}
              placeholder={translate('notifications.cancel_shipment.reason.placeholder')}
              value={reason}
              onChange={handleReasonChange}
              items={reasonOptions}
            />
            {isOtherSelected && (
              <TextField
                label={translate('notifications.cancel_shipment.reason.other_label')}
                placeholder={translate('notifications.cancel_shipment.reason.other_placeholder')}
                value={otherReasonText}
                onChange={(e) => setOtherReasonText(e.target.value)}
                containerWidth="full"
              />
            )}
          </Stack>
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline-primary" onClick={handleClose} disabled={cancelMutation.isPending}>
              {translate('notifications.cancel_shipment.actions.close')}
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isSubmitDisabled}
            data-testid="cancel-shipment-confirm-button"
          >
            {translate('notifications.cancel_shipment.actions.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
